import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  Building,
  Eye,
  EyeOff,
  Loader2,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { useRegisterUser } from '../api';
import { toLegacyExpiry } from '../format';
import { UsernameAvailability, type UsernameStatus } from '../components/UsernameAvailability';
import { createSubUserSchema, type CreateSubUserInput } from '../schema';
import { api } from '@/lib/api/client';

const STEP_FIELDS: Record<number, Array<keyof CreateSubUserInput>> = {
  0: ['username', 'password', 'confirmPassword', 'name', 'emailid', 'number'],
  1: ['company', 'address', 'pincode', 'expiryDate', 'reportType'],
  2: [],
};

const STEPS = [
  { id: 'account', label: 'Account', description: 'Login & contact', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company, expiry & report', icon: Building },
  { id: 'review', label: 'Review', description: 'Confirm details' },
] as const;

/** Report type options — matches old OBD dropdown */
const REPORT_TYPE_OPTIONS = [
  { value: '1', label: 'DTMF' },
  { value: '2', label: 'Call Patch' },
  { value: '3', label: 'Both' },
] as const;

export function CreateSubUserPage() {
  const navigate = useNavigate();
  const parentId = Number(useAuthStore((s) => s.userId));
  const [showPassword, setShowPassword] = useState(false);

  const register = useRegisterUser();
  const stepper = useStepper(STEPS.length);

  const form = useForm<CreateSubUserInput>({
    resolver: zodResolver(createSubUserSchema),
    mode: 'onTouched',
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      expiryDate: '',
      reportType: '1',
      reportDate: '',
    },
  });

  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const usernameValue = form.watch('username');

  const handleNext = async () => {
    if (stepper.current === 0 && (usernameStatus === 'unavailable' || usernameStatus === 'checking')) {
      if (usernameStatus === 'unavailable') {
        form.setError('username', { message: 'Username is not available' });
      }
      return;
    }
    const fields = STEP_FIELDS[stepper.current];
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: CreateSubUserInput) => {
    let reportDate: string | undefined;
    if (input.reportDate) {
      const rd = new Date(input.reportDate);
      const y = rd.getFullYear();
      const m = String(rd.getMonth() + 1).padStart(2, '0');
      const d = String(rd.getDate()).padStart(2, '0');
      reportDate = `${y}-${m}-${d} 23:59:59`;
    }

    register.mutate(
      {
        username: input.username.trim(),
        password: input.password,
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address?.trim() ?? '',
        pincode: input.pincode?.trim() ?? '',
        parent: parentId,
        accountType: 0, // hardcoded "0" — old OBD sends hidden field with "0"
        userType: 'sub_user',
        expiryDate: toLegacyExpiry(input.expiryDate),
        reportType: input.reportType,
        ...(reportDate ? { reportDate } : {}),
      },
      {
        onSuccess: async (res) => {
          if (res?.message && res.message.toLowerCase().includes('exist')) {
            form.setError('username', { message: res.message });
            stepper.goTo(0);
            return;
          }
          toast.success(res?.message ?? 'Sub user created');
          const newUserId = res?.userId;
          if (newUserId && newUserId !== '-1') {
            try {
              const parentSettings = await api.get<{ json: string | null }>(
                `/api/obd/user/${parentId}/ui/settings`,
              );
              if (parentSettings?.json) {
                await api.post(`/api/obd/user/${newUserId}/ui/settings`, {
                  json: parentSettings.json,
                });
              }
            } catch {
              // Silent fail — UI settings copy is best-effort
            }
          }
          navigate('/sub-user-list');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not create the sub user.');
        },
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Create Sub User"
        description="Create a sub user under your account. Plans and modules are inherited from your own assignments."
        actions={
          <Button asChild variant="outline">
            <Link to="/sub-user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
          <form className="space-y-4" noValidate>
          {stepper.current === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Account</CardTitle>
                <CardDescription>Login credentials and direct contact details.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Username<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="off" />
                      </FormControl>
                      <UsernameAvailability
                        username={usernameValue}
                        onStatusChange={setUsernameStatus}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Full name<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Password<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            className="pr-9"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Confirm password<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emailid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} type="email" autoComplete="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Phone<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))
                          }
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          autoComplete="tel"
                          placeholder="10-digit mobile number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Organization &amp; Settings</CardTitle>
                <CardDescription>Company details, account expiry, and report settings.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>
                        Address<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pincode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Pincode<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} inputMode="numeric" maxLength={10} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="expiryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        User Expiry<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ? new Date(field.value) : null}
                          onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                          fromDate={new Date()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reportType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Report Type<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Report Type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {REPORT_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reportDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Report Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ? new Date(field.value) : null}
                          onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                          fromDate={new Date()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 2 && <ReviewStep form={form} />}

          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || register.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/sub-user-list">Cancel</Link>
              </Button>
              <Button
                type="button"
                disabled={
                  register.isPending ||
                  (stepper.current === 0 &&
                    (usernameStatus === 'unavailable' || usernameStatus === 'checking'))
                }
                onClick={async () => {
                  if (!stepper.isLast) {
                    await handleNext();
                  } else {
                    form.handleSubmit(onSubmit)();
                  }
                }}
              >
                {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {stepper.isLast ? 'Create user' : <>Next <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

/* ------------------------------------------------------------------ */
/*  Review step                                                        */
/* ------------------------------------------------------------------ */

function ReviewStep({ form }: { form: ReturnType<typeof useForm<CreateSubUserInput>> }) {
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  const reportDate = v.reportDate ? formatDate(new Date(v.reportDate)) : '—';
  const reportTypeLabel =
    REPORT_TYPE_OPTIONS.find((o) => o.value === v.reportType)?.label ?? '—';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm the new sub user before creating. Click any earlier step header to edit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Account">
          <Row label="Username" value={v.username} />
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
        </ReviewSection>
        <ReviewSection title="Organization & Settings">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address ?? ''} multiline />
          <Row label="Pincode" value={v.pincode ?? ''} />
          <Row label="User Expiry" value={expiry} />
          <Row label="Report Type" value={reportTypeLabel} />
          <Row label="Report Date" value={reportDate} />
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}